// API service for ChatGPT Compliance API integration

export interface User {
  id: string;
  email?: string;
  name?: string;
  [key: string]: any;
}

export interface ExportJob {
  id?: string;
  userId: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  selectedUsers: string[];
  progress: number;
  totalItems: number;
  processedItems: number;
  error?: string;
  zipPassword?: string;
  createdAt: any;
  updatedAt: any;
  exportedData?: any;
}

const API_BASE = process.env.REACT_APP_FUNCTIONS_URL || "";

// Default workspace ID - can be overridden if needed
const DEFAULT_WORKSPACE_ID = "93f83389-574b-4ad8-8e41-494c31941728";

/**
 * Get organization users from ChatGPT Compliance API
 * Per OpenAPI spec: requires workspace_id
 */
export async function getOrganizationUsers(apiKey: string, workspaceId?: string): Promise<User[]> {
  const finalWorkspaceId = workspaceId || DEFAULT_WORKSPACE_ID;
  const params = new URLSearchParams();
  params.append("workspace_id", finalWorkspaceId);
  
  const response = await fetch(`${API_BASE}/getOrganizationUsers?${params.toString()}`, {
    method: "GET",
    headers: {
      "X-API-Key": apiKey,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    let errorMessage = "Failed to fetch users";
    try {
      const errorData = await response.json();
      console.log("API Error Response:", errorData);
      console.log("Error data type:", typeof errorData);
      console.log("Error data.error type:", typeof errorData?.error);
      
      // Handle different error response formats
      if (errorData.error) {
        if (typeof errorData.error === "string") {
          errorMessage = errorData.error;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
        } else if (typeof errorData.error === "object") {
          // Try to extract meaningful info from error object
          const errorObj = errorData.error;
          if (errorObj.code) {
            errorMessage = `Error ${errorObj.code}: ${errorObj.message || "Unknown error"}`;
          } else if (errorObj.type) {
            errorMessage = `${errorObj.type}: ${errorObj.message || JSON.stringify(errorObj)}`;
          } else {
            errorMessage = JSON.stringify(errorObj);
            // If it's too long or just {}, use a generic message
            if (errorMessage.length > 200 || errorMessage === "{}") {
              errorMessage = `API Error (${response.status}): Invalid API key or request failed`;
            }
          }
        } else {
          errorMessage = String(errorData.error);
        }
      } else if (errorData.message) {
        errorMessage = typeof errorData.message === "string" 
          ? errorData.message 
          : JSON.stringify(errorData.message);
      } else if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (typeof errorData === "object") {
        // If the whole response is an object without error/message, try to extract info
        if (errorData.code) {
          errorMessage = errorData.message 
            ? `Error ${errorData.code}: ${errorData.message}`
            : `Error ${errorData.code}`;
        } else {
          // Don't stringify the whole object - use a generic message
          errorMessage = `API Error (${response.status}): ${response.statusText || "Request failed"}`;
        }
      } else {
        errorMessage = String(errorData);
      }
    } catch (parseError) {
      // If response is not JSON, use status text
      console.error("Failed to parse error response:", parseError);
      errorMessage = `API Error (${response.status}): ${response.statusText || "Request failed"}`;
    }
    console.log("Final error message:", errorMessage);
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.users || [];
}

/**
 * Start export for selected users
 * Per OpenAPI spec: requires workspaceId
 */
export async function exportUserContent(
    apiKey: string,
    selectedUsers: string[],
    userId: string,
    workspaceId?: string
): Promise<{jobId: string; status: string; message: string}> {
  const finalWorkspaceId = workspaceId || DEFAULT_WORKSPACE_ID;
  const response = await fetch(`${API_BASE}/exportUserContent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      apiKey,
      selectedUsers,
      userId,
      workspaceId: finalWorkspaceId,
    }),
  });

  if (!response.ok) {
    let errorMessage = "Failed to start export";
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = typeof errorData.error === "string" 
          ? errorData.error 
          : errorData.error.message || JSON.stringify(errorData.error);
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = `API Error (${response.status}): ${response.statusText || "Request failed"}`;
      }
    } catch {
      errorMessage = `API Error (${response.status}): ${response.statusText || "Request failed"}`;
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

/**
 * Get export job status
 */
export async function getExportJobStatus(
    jobId?: string,
    userId?: string
): Promise<{job?: ExportJob; jobs?: ExportJob[]}> {
  const params = new URLSearchParams();
  if (jobId) params.append("jobId", jobId);
  if (userId) params.append("userId", userId);

  const response = await fetch(
      `${API_BASE}/getExportJobStatus?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
  );

  if (!response.ok) {
    let errorMessage = "Failed to get job status";
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = typeof errorData.error === "string" 
          ? errorData.error 
          : errorData.error.message || JSON.stringify(errorData.error);
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = `API Error (${response.status}): ${response.statusText || "Request failed"}`;
      }
    } catch {
      errorMessage = `API Error (${response.status}): ${response.statusText || "Request failed"}`;
    }
    throw new Error(errorMessage);
  }

  return await response.json();
}

/**
 * Download export data as password-protected ZIP
 */
export async function downloadExportData(jobId: string): Promise<void> {
  const response = await fetch(
      `${API_BASE}/downloadExportData?jobId=${jobId}`,
      {
        method: "GET",
      }
  );

  if (!response.ok) {
    // Try to get error message as JSON
    try {
      const error = await response.json();
      throw new Error(error.error || "Failed to download export");
    } catch {
      throw new Error("Failed to download export");
    }
  }

  // Get ZIP file as blob
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `export_${jobId}.zip`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

